import React, { useState } from "react";
import { Form, Input, Button, Checkbox, Dropdown, message } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone, GlobalOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { BackendLogin } from "../../api/Auth";
import useAuthStore from "../../store/authStore";
import { getDefaultRoute } from "../../config/permissions";

export default function LoginPage() {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const { setAuth } = useAuthStore();

    const currentLang = i18n.language;

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
    };

    const languageItems = {
        items: [
            {
                key: 'vi',
                label: (
                    <div className="flex items-center gap-2" onClick={() => changeLanguage('vi')}>
                        <img src="/images/flag/vi.png" alt="VI" className="w-5 h-4 object-cover rounded-sm" />
                        <span>Tiếng Việt</span>
                    </div>
                ),
            },
            {
                key: 'en',
                label: (
                    <div className="flex items-center gap-2" onClick={() => changeLanguage('en')}>
                        <img src="/images/flag/en.png" alt="EN" className="w-5 h-4 object-cover rounded-sm" />
                        <span>English</span>
                    </div>
                ),
            },
        ],
    };

    const onFinish = async (values: { email: string; password: string }) => {
        setLoading(true);
        try {
            const res = await BackendLogin(values.email, values.password);
            if (res.status && res.data) {
                message.success(t('login.success'));
                const userType = res.data.type;
                const perms = Array.isArray(res.data.info?.role?.permissions)
                    ? res.data.info.role.permissions
                    : [];
                setAuth(userType, perms);
                navigate(getDefaultRoute(perms, userType === 1));
            } else {
                message.error(res.msg || t('login.error'));
            }
        } catch {
            message.error(t('login.error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>{t('login.welcome')} | Khothi</title>
            </Helmet>
            <div className="min-h-screen px-2 flex items-center auth-wrapper">
                {/* Language Switcher */}            <div className="absolute top-4 right-4 z-10">
                    <Dropdown menu={languageItems} placement="bottomRight" trigger={['click']}>
                        <Button
                            className="flex items-center gap-2 !border-gray-200 !shadow-sm hover:!border-[#3e91ab]"
                            size="middle"
                        >
                            <img
                                src={`/images/flag/${currentLang}.png`}
                                alt={currentLang}
                                className="w-5 h-4 object-cover rounded-sm"
                            />
                            <span className="text-sm">{currentLang === 'vi' ? 'VI' : 'EN'}</span>
                        </Button>
                    </Dropdown>
                </div>

                <div className="w-full flex justify-center min-h-screen">

                    {/* LEFT IMAGE */}
                    <div className="hidden lg:flex items-center justify-center p-6 w-[42%] ml-auto">
                        <img
                            src="https://nexlink.layoutdrop.com/php/demo/assets/images/auth/vector2.svg"
                            alt="auth"
                            className="w-[554px] mx-auto img-fluid"
                        />
                    </div>

                    {/* RIGHT FORM */}
                    <div className="flex items-center justify-center px-3 py-3 lg:px-6 lg:py-8 w-full lg:w-[42%] ml-auto">
                        <div className="bg-white w-full max-w-[450px] rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.05)] lg:p-[40px] p-4">

                            {/* Logo */}
                            <div className="text-center mb-6">
                                <a href="/" aria-label="Kho Thi logo">
                                    <img
                                        src="/images/logo.png"
                                        alt="Kho Thi logo"
                                        className="mx-auto !h-[40px]"
                                    />
                                </a>
                            </div>

                            {/* Title */}
                            <div className="text-center mb-[40px]">
                                <h5 className="text-lg !font-semibold !mb-0">
                                    {t('login.welcome')}
                                </h5>
                                <p className="text-gray-500 text-sm">
                                    {t('login.subtitle')}
                                </p>
                            </div>

                            {/* FORM */}
                            <Form form={form} layout="vertical" onFinish={onFinish}>
                                {/* Email */}
                                <Form.Item
                                    label={t('login.email')}
                                    name="email"
                                    rules={[
                                        { required: true, message: t('login.email_required') },
                                        { type: "email", message: t('login.email_invalid') },
                                    ]}
                                >
                                    <Input
                                        size="large"
                                        placeholder={t('login.email_placeholder')}
                                        className="rounded-lg"
                                    />
                                </Form.Item>

                                {/* Password */}
                                <Form.Item
                                    label={t('login.password')}
                                    name="password"
                                    rules={[{ required: true, message: t('login.password_required') }]}
                                >
                                    <Input.Password
                                        size="large"
                                        placeholder="********"
                                        iconRender={(visible) =>
                                            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                                        }
                                        className="rounded-lg"
                                    />
                                </Form.Item>

                                {/* Remember + Forgot */}
                                <Form.Item>
                                    <div className="flex items-center justify-between">
                                        <Form.Item
                                            name="terms"
                                            valuePropName="checked"
                                            noStyle
                                        >
                                            <Checkbox
                                                className="
                                                [&_.ant-checkbox-inner]:!w-[21px]
                                                [&_.ant-checkbox-inner]:!h-[21px]
                                                [&_.ant-checkbox-inner]:!rounded-md
                                                [&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-[#3e91ab]
                                                [&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-[#3e91ab]
                                            "
                                            >
                                                {t('login.remember')}
                                            </Checkbox>
                                        </Form.Item>

                                        <a
                                            href="/forgot-password"
                                            className="text-sm !text-[#357f96] hover:!text-yellow-700 transition"
                                        >
                                            {t('login.forgot')}
                                        </a>
                                    </div>
                                </Form.Item>

                                {/* Submit */}
                                <Form.Item className="!mb-3">
                                    <Button
                                        htmlType="submit"
                                        loading={loading}
                                        size="large"
                                        type="primary"
                                        className="w-full h-11 rounded-lg !bg-[#3e91ab] !text-sm hover:!bg-[#357f96] border-none"
                                    >
                                        {t('login.signin')}
                                    </Button>
                                </Form.Item>

                                {/* <p className="text-center text-sm text-gray-500 !mb-9">
                                {t('login.no_account')}{" "}
                                <a href="/register" className="!text-[#357f96]">
                                    {t('login.signup')}
                                </a>
                            </p>

                            <div className="relative text-center !my-6">
                                <div className="border-b border-b-[#EEEEF3]"></div>
                                <span className="absolute left-1/2 bg-white px-3 top-0" style={{ transform: "translate(-50%, -50%)" }}>
                                    {t('login.or_continue')}
                                </span>
                            </div>

                            <Button
                                size="large"
                                className="
                                    flex items-center justify-center gap-2
                                    !bg-[#f2f2f6]
                                    w-full h-11 rounded-lg
                                    !border-none
                                    text-gray-800
                                    hover:!bg-[#e9ecef]
                                    hover:!border-[#e9ecef]
                                    active:!bg-[#dee2e6]
                                    transition-all
                                    !text-sm
                                    !text-[#0c2c3c]
                                    !font-medium
                                "
                            >
                                <img
                                    src="https://nexlink.layoutdrop.com/php/demo/assets/images/icons/google.svg"
                                    alt="google"
                                    className="w-5"
                                />
                                {t('login.google')}
                            </Button> */}

                            </Form>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
}